/**
 * AvailabilitySelectPopoverContent — unified batch-action popover shown when
 * one or more availability-grid cells are selected (free/unknown cells,
 * the caller's own reserved-own/option-own cells, or a mix of both).
 *
 * Part of the unified INSPECT-vs-SELECT interaction proposal (see
 * apps/mockups/src/screens/availability-unified.mockup.tsx). Scaffolded here
 * ahead of the real spec so the mockup can consume it via the standard
 * mockup -> apps/web dependency direction.
 *
 * The concrete action buttons (Rezerwacja / Opcja / Zwolnij) are NOT
 * committed business logic yet — the proposal is still under review — so
 * they are passed in via the `actions` slot rather than hardcoded here.
 * `onClear` is real, already-working behavior (clearing the local
 * selection), so it stays a first-class callback prop.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ooh/ui/components/select';

export interface AvailabilitySelectPopoverContentProps {
  periodCount: number;
  unitCount: number;
  offerValue?: string;
  offerOptions?: readonly string[];
  onOfferChange?: (value: string) => void;
  /**
   * Overrides the built-in `offerValue`/`offerOptions` <Select> with caller-supplied
   * markup — e.g. the real `QuickAddOfferPicker` (`presentation="popover"`) wired by
   * `UnifiedSelectPopover`. Pass `null` to omit the offer step entirely (the grid is
   * already scoped to a single offer, per the `unified-availability-interaction` spec's
   * "offer workspace context" rule). Omit this prop to keep the mockup's plain-Select
   * rendering (backward-compatible default).
   */
  offerSection?: ReactNode;
  /**
   * Expiry-deadline field for a single own-held occupied cell (SOFT/HARD reservation) —
   * availability-calendar spec's "the cell popover SHALL be the guaranteed path to a
   * reservation's expiry deadline ... in every case", no carve-out for own-held cells
   * that route to this SELECT popover instead of INSPECT. `undefined` renders no row at
   * all: a genuinely free cell being selected for a new reservation has no deadline yet,
   * and a multi-cell selection has no single deadline to report — the caller (
   * `UnifiedSelectPopover`) only ever supplies this for a single-cell own-held selection.
   */
  deadlineNode?: ReactNode;
  /**
   * The last Rezerwacja outcome (reserved/queued/failed groups) — rendered above the
   * actions grid, which stays usable underneath so the caller can act on the recomputed
   * selection (e.g. Zwolnij the queue entries a mixed Rezerwacja just joined). `undefined`
   * renders nothing (task 4.2, `reservation-mixed-range-and-target-expiry`).
   */
  resultNode?: ReactNode;
  /**
   * The own-held single-cell "Dodaj wpis dla innego klienta" queue-join slot
   * (CU-86cbejhae) — the real `QueueJoinPanel` node for an exactly-one-own-held-cell
   * selection, or `UnifiedSelectPopover`'s own disabled+tooltip placeholder for a
   * multi-cell selection containing an own-held cell. `undefined` renders nothing:
   * the selection has no own-held cell at all.
   */
  queueJoinSection?: ReactNode;
  /** Rezerwacja / Opcja / Zwolnij action buttons — caller-supplied (see file header). */
  actions: ReactNode;
  onClear: () => void;
}

export function AvailabilitySelectPopoverContent({
  periodCount,
  unitCount,
  offerValue,
  offerOptions,
  onOfferChange,
  offerSection,
  deadlineNode,
  resultNode,
  queueJoinSection,
  actions,
  onClear,
}: AvailabilitySelectPopoverContentProps) {
  const { t } = useTranslation('inventory');

  const builtInOfferSelect =
    offerValue !== undefined && offerOptions !== undefined && onOfferChange !== undefined ? (
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-2xs text-muted-foreground whitespace-nowrap">
          {t('availability.select.offerFieldLabel')}:
        </span>
        <Select value={offerValue} onValueChange={onOfferChange}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue>{offerValue}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {offerOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ) : null;

  return (
    <div className="p-3" data-testid="availability-select-popover">
      <p className="text-xs font-medium text-foreground mb-2.5">
        {t('availability.select.summaryPrefix')}:{' '}
        <span className="font-semibold">
          {t('availability.select.periodsCount', { count: periodCount })}
        </span>{' '}
        ·{' '}
        <span className="font-semibold">
          {t('availability.select.unitsCount', { count: unitCount })}
        </span>
      </p>

      {deadlineNode !== undefined ? (
        <p className="mb-2.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          {t('availability.inspect.expiresLabel')}:{' '}
          <span className="font-medium text-foreground" data-testid="availability-select-ttl-label">
            {deadlineNode}
          </span>
        </p>
      ) : null}

      {resultNode}

      {offerSection !== undefined ? offerSection : builtInOfferSelect}

      {queueJoinSection ? <div className="mb-2.5">{queueJoinSection}</div> : null}

      <div className="flex flex-col pt-2 border-t border-border">{actions}</div>

      <div className="pt-2 border-t border-border">
        <Button
          type="button"
          variant="link"
          size="xs"
          className="h-auto p-0 text-2xs"
          onClick={onClear}
          data-testid="clear-selection-btn"
        >
          {t('availability.select.clearBtn')}
        </Button>
      </div>
    </div>
  );
}
